/**
 * Scenario page sub-components. Split out from Scenario.jsx so each piece is
 * small, isolated, and easy to reason about.
 */
import { Link } from "react-router-dom";
import { CheckCircle2, CircleDot, ArrowRight, Sparkles } from "lucide-react";

const TONE_STYLES = {
    approve: "border-emerald-700 text-emerald-900 hover:bg-emerald-50",
    defer: "border-amber-700 text-amber-900 hover:bg-amber-50",
    reject: "border-rose-700 text-rose-900 hover:bg-rose-50",
    escalate: "border-slate-700 text-slate-900 hover:bg-slate-100",
};

function stepIcon(isActive, isDone) {
    if (isDone)
        return <CheckCircle2 size={12} strokeWidth={1.5} className="text-emerald-600" />;
    if (isActive)
        return <CircleDot size={12} strokeWidth={1.5} className="text-slate-900" />;
    return null;
}

function stepBorderClass(isActive, isDone) {
    if (isActive) return "border-slate-900";
    if (isDone) return "border-emerald-600";
    return "border-slate-200";
}

export function Stepper({ steps, active, done, remapRole = (r) => r }) {
    return (
        <ol
            className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-2"
            data-testid="scenario-stepper"
        >
            {steps.map((s, i) => {
                const isActive = i === active && !done;
                const isDone = done || i < active;
                return (
                    <li
                        key={s.id}
                        className={`border-l-2 pl-3 py-1 ${stepBorderClass(isActive, isDone)}`}
                    >
                        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">
                            Step {String(i + 1).padStart(2, "0")}
                            {stepIcon(isActive, isDone)}
                        </div>
                        <div className="text-sm font-semibold text-slate-900 leading-tight mt-1">
                            {remapRole(s.role)}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}

function AiAdvisoryBlock({ advisory }) {
    if (!advisory) return null;
    const list =
        advisory.suggested_controls ||
        advisory.suggested_response ||
        advisory.watch_outs ||
        [];
    return (
        <div className="mt-6 surface-subtle border-l-4 border-slate-900 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} strokeWidth={1.5} className="text-slate-700" />
                <span className="overline">{advisory.label}</span>
            </div>
            {advisory.risk_category && (
                <div className="text-sm text-slate-800 mb-1">
                    <strong>Risk category:</strong> {advisory.risk_category}
                </div>
            )}
            {advisory.suggested_pattern && (
                <div className="text-sm text-slate-800 mb-1">
                    <strong>Suggested pattern:</strong> {advisory.suggested_pattern}
                </div>
            )}
            {list.length > 0 && (
                <ul className="list-disc pl-5 text-sm text-slate-700 mt-2 space-y-1">
                    {list.map((c) => (
                        <li key={c}>{c}</li>
                    ))}
                </ul>
            )}
            {advisory.note && (
                <p className="text-xs italic text-slate-500 mt-3">{advisory.note}</p>
            )}
        </div>
    );
}

export function StepCard({ step, index, total, remapRole = (r) => r }) {
    if (!step) return null;
    return (
        <section
            className="tile rounded-sm p-6 lg:p-8"
            data-testid={`step-card-${step.id}`}
        >
            <div className="flex items-center justify-between mb-4">
                <span className="overline">
                    Step {index + 1} of {total} · {remapRole(step.role)}
                </span>
                {step.decision_required && (
                    <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-amber-700">
                        Human decision gate
                    </span>
                )}
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight mb-3">
                {step.title}
            </h2>
            <p className="text-base text-slate-700 leading-relaxed mb-6">
                {step.summary}
            </p>

            {step.fields && (
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 border-t border-slate-200 pt-5">
                    {Object.entries(step.fields).map(([k, v]) => (
                        <div key={k} className="border-b border-slate-100 pb-2">
                            <dt className="overline mb-1">{k}</dt>
                            <dd className="text-sm text-slate-800 leading-relaxed">
                                {v}
                            </dd>
                        </div>
                    ))}
                </dl>
            )}

            <AiAdvisoryBlock advisory={step.ai_advisory} />
        </section>
    );
}

export function DecisionGate({ outcomes, onSubmit, submitting }) {
    return (
        <section className="tile rounded-sm p-6 lg:p-8" data-testid="decision-gate">
            <div className="overline mb-2">Accountable decision</div>
            <h3 className="text-xl lg:text-2xl font-bold tracking-tight mb-2">
                Take the decision. KATLAS will sign it.
            </h3>
            <p className="text-sm text-slate-600 mb-6 max-w-2xl">
                AI advisories above are advisory only. The named human role retains
                accountable authority. Pick one outcome to commit it to the receipt.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {outcomes.map((o) => (
                    <button
                        key={o.id}
                        disabled={submitting}
                        onClick={() => onSubmit(o)}
                        className={`text-left rounded-sm border-2 px-5 py-4 transition-colors duration-200 disabled:opacity-50 ${TONE_STYLES[o.tone] || TONE_STYLES.escalate}`}
                        data-testid={`decision-${o.id}`}
                    >
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-1 opacity-70">
                            {o.tone}
                        </div>
                        <div className="font-semibold tracking-tight">{o.label}</div>
                    </button>
                ))}
            </div>
            {submitting && (
                <p className="mt-4 text-xs font-mono text-slate-500">
                    Signing receipt via KATLAS node…
                </p>
            )}
        </section>
    );
}

export function DecisionSummary({ run, scenario }) {
    const link = `${window.location.origin}/snapshot/${run.snapshot_token}`;
    const briefLink = `/brief/${run.snapshot_token}`;
    return (
        <section className="tile rounded-sm p-6 lg:p-8" data-testid="decision-summary">
            <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={18} strokeWidth={1.5} className="text-emerald-600" />
                <span className="overline">Decision recorded</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight">
                {run.decision?.decision_label}
            </h2>
            <p className="text-sm text-slate-600 mt-2">
                Authorised by {run.decision?.accountable_role}. Scenario:{" "}
                {scenario.title}.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
                <a
                    href={briefLink}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2"
                    data-testid="print-lab-brief-btn"
                >
                    Print Lab Brief ↗
                </a>
                <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost text-sm px-4 py-2 rounded-sm"
                    data-testid="snapshot-link"
                >
                    Read-only customer snapshot ↗
                </a>
                <Link
                    to="/receipts"
                    className="btn-ghost text-sm px-4 py-2 rounded-sm"
                    data-testid="go-to-receipts-btn"
                >
                    View all receipts
                </Link>
            </div>
        </section>
    );
}

export function MiniBlock({ label, value }) {
    return (
        <div className="mb-3 last:mb-0">
            <div className="overline mb-1">{label}</div>
            <p className="text-sm text-slate-700 leading-relaxed">{value}</p>
        </div>
    );
}

export function ContinueButton({ onClick }) {
    return (
        <div className="flex justify-end">
            <button
                className="btn-primary px-5 py-2.5 rounded-sm text-sm inline-flex items-center gap-2"
                onClick={onClick}
                data-testid="next-step-btn"
            >
                Continue <ArrowRight size={14} strokeWidth={1.5} />
            </button>
        </div>
    );
}
